import { useEffect, useState } from 'react';
import { Bar, BarChart, CartesianGrid, Cell, LabelList, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import PageLayout from '../components/PageLayout';
import ImportModal from '../components/ImportModal';
import EmptyState from '../components/EmptyState';
import { IconTrendingUp, IconReceipt, IconWallet, IconUsers } from '../components/icons';
import { useCompany } from '../context/CompanyContext';
import { fyLabel } from '../utils/fy';
import api from '../api';
import './Dashboard.css';

const CHART_PRIOR = '#f5b98a';
const CHART_ACCENT = '#e8720c';

const MONTH_SHORT = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];

function inr(amount) {
  return `₹${Number(amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function lakh(amount) {
  return `₹${(Number(amount || 0) / 100000).toFixed(1)}L`;
}

function inFy(month, year, fyStartYear) {
  return (month >= 4 && year === fyStartYear) || (month <= 3 && year === fyStartYear + 1);
}

function quarterInFy(quarter, year, fyStartYear) {
  if (quarter === 'Q4') return year === fyStartYear + 1;
  return year === fyStartYear;
}

function Financials() {
  const { selectedCompanyId, fyStartYear } = useCompany();
  const [financialData, setFinancialData] = useState([]);
  const [gstData, setGstData] = useState([]);
  const [tdsData, setTdsData] = useState([]);
  const [tdsDeposits, setTdsDeposits] = useState([]);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [importOpen, setImportOpen] = useState(false);

  const loadAll = () => {
    if (!selectedCompanyId) return undefined;
    setLoading(true);
    return Promise.all([
      api.get(`/financial-data/${selectedCompanyId}`),
      api.get(`/gst-data/${selectedCompanyId}`, { params: { fy_year: fyStartYear } }),
      api.get(`/tds-data/${selectedCompanyId}`),
      api.get(`/tds-deposits/${selectedCompanyId}`, { params: { fy_year: fyStartYear } }),
    ])
      .then(([finRes, gstRes, tdsRes, tdsDepositRes]) => {
        setFinancialData(finRes.data);
        setGstData(gstRes.data);
        setTdsData(tdsRes.data);
        setTdsDeposits(tdsDepositRes.data);
        setError(null);
      })
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCompanyId, fyStartYear]);

  const fyFinancials = financialData.filter((r) => inFy(r.month, r.year, fyStartYear));
  const revenueYtd = fyFinancials.reduce((sum, r) => sum + r.revenue, 0);
  const ebitdaYtd = fyFinancials.reduce((sum, r) => sum + r.ebitda, 0);
  const ebitdaMargin = revenueYtd > 0 ? Math.round((ebitdaYtd / revenueYtd) * 100) : 0;

  const sortedAll = [...financialData].sort((a, b) => (a.year - b.year) || (a.month - b.month));
  const latest = sortedAll[sortedAll.length - 1];
  const netReceivables = latest ? latest.receivables : 0;
  const cashBalance = latest ? latest.cash_balance : 0;

  const chartMonths = sortedAll.slice(-6);
  const chartData = chartMonths.map((r) => {
    const value = r.revenue / 100000;
    return {
      label: `${MONTH_SHORT[r.month - 1]} ${String(r.year).slice(2)}`,
      value,
      valueLabel: `₹${value.toFixed(1)}L`,
    };
  });

  // "Pending" here is driven by the REAL deposit records (TdsDeposit, same
  // source the Dashboard uses), not TdsData's own deposit_status field -
  // that field was never linked to the actual TDS/TCS page and could
  // silently disagree with it. A quarter counts as pending if any of its
  // months isn't yet marked Deposited there.
  const quarterFullyDeposited = (quarter) => {
    const monthsInQuarter = tdsDeposits.filter((d) => d.quarter === quarter);
    return monthsInQuarter.length > 0 && monthsInQuarter.every((d) => d.status === 'Deposited');
  };

  const gstPayable = gstData.reduce((sum, r) => sum + r.net_payable, 0);
  const tdsPending = tdsData
    .filter((r) => quarterInFy(r.quarter, r.year, fyStartYear) && !quarterFullyDeposited(r.quarter))
    .reduce((sum, r) => sum + r.tds_amount, 0);
  const totalTaxLiability = gstPayable + tdsPending;

  const taxTotal = gstPayable + tdsPending || 1;
  const taxBreakdown = [
    { label: 'GST payable', amount: gstPayable, pct: Math.round((gstPayable / taxTotal) * 100), color: 'orange' },
    { label: 'TDS pending', amount: tdsPending, pct: Math.round((tdsPending / taxTotal) * 100), color: 'amber' },
  ];

  return (
    <PageLayout>
      <div className="page-title-row">
        <div>
          <h1 className="page-title">Financial Dashboard</h1>
          <p className="page-subtitle">Built from your Data Entry records — {fyLabel(fyStartYear)}</p>
        </div>
        <button className="dash-btn cal-btn-grey" type="button" onClick={() => setImportOpen(true)}>
          Import
        </button>
      </div>

      {error && <p className="dash-error">Backend unreachable: {error}</p>}

      <div className={`dash-compact ${loading ? 'dash-refreshing' : ''}`}>
        <div className="dash-stat-grid dash-stat-grid-5">
          <div className="dash-stat-card">
            <div className="dash-stat-icon green"><IconTrendingUp /></div>
            <div className="dash-stat-value dash-serif green">{lakh(revenueYtd)}</div>
            <div className="dash-stat-label">Revenue YTD</div>
            <div className="dash-stat-sub">{fyLabel(fyStartYear)}</div>
          </div>
          <div className="dash-stat-card">
            <div className="dash-stat-icon green"><IconTrendingUp /></div>
            <div className="dash-stat-value dash-serif green">{lakh(ebitdaYtd)}</div>
            <div className="dash-stat-label">EBITDA</div>
            <div className="dash-stat-sub">{ebitdaMargin}% margin</div>
          </div>
          <div className="dash-stat-card">
            <div className={`dash-stat-icon ${totalTaxLiability > 0 ? 'red' : 'grey'}`}><IconReceipt /></div>
            <div className={`dash-stat-value dash-serif ${totalTaxLiability > 0 ? 'red' : 'grey'}`}>
              {lakh(totalTaxLiability)}
            </div>
            <div className="dash-stat-label">Total Tax Liability</div>
            <div className="dash-stat-sub">GST + pending TDS</div>
          </div>
          <div className="dash-stat-card">
            <div className="dash-stat-icon teal"><IconWallet /></div>
            <div className="dash-stat-value dash-serif green">{lakh(cashBalance)}</div>
            <div className="dash-stat-label">Cash Balance</div>
            <div className="dash-stat-sub">{latest ? `As of ${MONTH_SHORT[latest.month - 1]} ${latest.year}` : '—'}</div>
          </div>
          <div className="dash-stat-card">
            <div className="dash-stat-icon amber"><IconUsers /></div>
            <div className="dash-stat-value dash-serif amber">{lakh(netReceivables)}</div>
            <div className="dash-stat-label">Net Receivables</div>
            <div className="dash-stat-sub">{latest ? `As of ${MONTH_SHORT[latest.month - 1]} ${latest.year}` : '—'}</div>
          </div>
        </div>

        <div className="dash-two-col">
          <div className="dash-card">
            <h2 className="dash-card-title">Monthly Revenue (₹ Lakh)</h2>
            {chartData.length === 0 ? (
              <EmptyState message="No financial data entered yet." ctaLabel="Go to Data Entry" to="/data-entry" />
            ) : (
              <>
                <div className="dash-chart-legend">
                  <span className="dash-chart-legend-dot" style={{ background: CHART_PRIOR }} /> Prior months
                  <span className="dash-chart-legend-dot" style={{ background: CHART_ACCENT, marginLeft: 10 }} /> Latest month
                </div>
                <div className="dash-chart-frame">
                  <ResponsiveContainer width="100%" height={220}>
                    <BarChart data={chartData} margin={{ top: 22, right: 8, left: 0, bottom: 0 }}>
                      <CartesianGrid vertical={false} stroke="#eef0f3" />
                      <XAxis dataKey="label" tick={{ fontSize: 12, fill: '#6b7280' }} axisLine={false} tickLine={false} />
                      <YAxis tick={{ fontSize: 12, fill: '#6b7280' }} axisLine={false} tickLine={false} width={36} />
                      <Tooltip formatter={(value) => [`₹${value.toFixed(1)}L`, 'Revenue']} cursor={{ fill: 'rgba(15,23,42,0.03)' }} />
                      <Bar dataKey="value" radius={[6, 6, 0, 0]} maxBarSize={46}>
                        {chartData.map((entry, index) => (
                          <Cell key={entry.label} fill={index === chartData.length - 1 ? CHART_ACCENT : CHART_PRIOR} />
                        ))}
                        <LabelList
                          dataKey="valueLabel"
                          position="top"
                          offset={6}
                          style={{ fontSize: 11, fontWeight: 700, fill: '#6b7280' }}
                        />
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </>
            )}
          </div>

          <div className="dash-card">
            <h2 className="dash-card-title">Tax liability breakdown</h2>
            {gstData.length === 0 && tdsData.length === 0 ? (
              <EmptyState message="No GST or TDS data entered yet." icon={IconReceipt} />
            ) : (
              taxBreakdown.map((row) => (
                <div className="dash-progress-row dash-progress-row-stacked" key={row.label}>
                  <div className="dash-progress-label">
                    {row.label}: {inr(row.amount)}
                  </div>
                  <div className="dash-progress-track">
                    <div
                      className={`dash-progress-fill dash-progress-${row.color}`}
                      style={{ width: `${row.pct}%` }}
                    />
                  </div>
                  <div className="dash-progress-pct">{row.pct}%</div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      <ImportModal
        open={importOpen}
        onClose={() => setImportOpen(false)}
        module="financials"
        moduleLabel="Financials"
        companyId={selectedCompanyId}
        onImported={loadAll}
      />
    </PageLayout>
  );
}

export default Financials;
